import { Button, Icon, InlineStack, Spinner, Tooltip } from '@customerio/pluma-components/react';
import { useFormContext, useWatch } from '@/components/form';
import type { Integration, IntegrationSetting } from '@/services/db/integration/entity';
import { useVerifyDataOutIntegration } from '@/services/db/data-out-integration/mutations';
import { hasAllSecrets, type DataOutFormValues } from '../form/schema';

type Snackbars = { create: (options: { variant: 'default' | 'error'; id: string; message: string }) => () => void };

type TestConnectionButtonProps = {
  integration: Integration;
  settings: readonly IntegrationSetting[];
  isFormDisabled: boolean;
  snackbars: Snackbars;
};

/**
 * Sends a sample event through the connector's current settings.
 *
 * The result is transient feedback beside the button, not saved state. A failed test comes back as
 * a 400, so success and failure are the mutation's two outcomes rather than a field on the response.
 */
export function TestConnectionButton({ integration, settings, isFormDisabled, snackbars }: TestConnectionButtonProps) {
  const { control, trigger, getValues } = useFormContext<DataOutFormValues>();
  const currentSettings = useWatch({ control, name: 'settings' });
  const verify = useVerifyDataOutIntegration();

  // Every secret must be present even on a connected connector: the API never returns stored
  // secrets, so it cannot authenticate a test from the saved record alone.
  const isDisabled = isFormDisabled || verify.isPending || !hasAllSecrets(settings, currentSettings);

  const onTest = async () => {
    const isFormValid = await trigger('settings');
    if (!isFormValid || isDisabled) {
      return;
    }

    try {
      const result = await verify.mutateAsync({
        identifier: integration.id,
        settings: getValues('settings'),
      });

      if (result.alert != null) {
        snackbars.create({
          variant: 'default',
          id: 'verify.data-out.success',
          message: `${result.alert.title}: ${result.alert.content}`,
        });
      }
    } catch {
      // The icon carries the failure; the endpoint's message is not user-actionable copy.
    }
  };

  return (
    <InlineStack alignBlock="center" gap="150" mt="200">
      <Tooltip isDisabled={!isDisabled} content="Please fill out the form to test the connection" isCustom={true}>
        {(context) => (
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            variant="secondary"
            isDisabled={isDisabled}
            onClick={() => {
              void onTest();
            }}
          >
            Test connection
          </Button>
        )}
      </Tooltip>
      <TestResult isPending={verify.isPending} isSuccess={verify.isSuccess} isError={verify.isError} />
    </InlineStack>
  );
}

type TestResultProps = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
};

function TestResult({ isPending, isSuccess, isError }: TestResultProps) {
  if (isPending) {
    return <Spinner data-test-verify-result="pending" />;
  }
  if (isSuccess) {
    return <Icon name="check-circle" color="success" data-test-verify-result="success" />;
  }
  if (isError) {
    return <Icon name="error" color="critical" data-test-verify-result="error" />;
  }
  return null;
}
